/**
 * Studio Boran - Song Camp Participant Matching System
 * © 2026 Studio Boran (THAIDAI) - Proprietary & Confidential
 *
 * Unauthorized copying, modification, or distribution is prohibited.
 * See LICENSE.md for full terms.
 *
 * Contact: 2nnerchild@naver.com | Instagram: @2nner_child
 */

import { useState, useEffect, useRef } from "react";
import { initializeApp } from "firebase/app";
import { getFirestore, collection, getDocs, doc, setDoc, deleteDoc } from "firebase/firestore";

const firebaseConfig = {
  apiKey: "AIzaSyDweHdSa0fHTimWOjc1DMCyd4aatVZwdys",
  authDomain: "song-camp-role-finder.firebaseapp.com",
  projectId: "song-camp-role-finder",
  storageBucket: "song-camp-role-finder.firebasestorage.app",
  messagingSenderId: "171473027326",
  appId: "1:171473027326:web:2bc51bb1396b1e8e3765af"
};

const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD || "";
const TOTAL_QUESTIONS = 25;

let db = null;

async function initFirebase() {
  try {
    const app = initializeApp(firebaseConfig);
    db = getFirestore(app);
    return true;
  } catch { return false; }
}

async function fbSave(p) {
  try {
    if (!db) return false;
    const ref = doc(db, "participants", p.email.replace(/[.@]/g, "_"));
    await setDoc(ref, p);
    return true;
  } catch { return false; }
}

async function fbDelete(email) {
  try {
    if (!db) return false;
    const ref = doc(db, "participants", email.replace(/[.@]/g, "_"));
    await deleteDoc(ref);
    return true;
  } catch { return false; }
}

async function loadAll() {
  try {
    if (!db) return [];
    const snap = await getDocs(collection(db, "participants"));
    return snap.docs.map(d => {
      const data = d.data();
      // multiAnswers was stored as JSON string to avoid Firestore nested array restriction
      if (typeof data.multiAnswers === "string") {
        try { data.multiAnswers = JSON.parse(data.multiAnswers); } catch {}
      }
      return { id: d.id, ...data };
    });
  } catch { return []; }
}

async function saveP(p) {
  await fbSave(p);
  return loadAll();
}

async function checkMyPreviousResult() {
  try {
    const email = localStorage.getItem("sb_my_email");
    if (!email || !db) return null;
    const list = await loadAll();
    return list.find(p => p.email === email) || null;
  } catch { return null; }
}

async function clearMyResult(email) {
  try {
    localStorage.removeItem("sb_my_email");
    const ref = doc(db, "participants", email.replace(/[.@]/g, "_"));
    await deleteDoc(ref);
  } catch {}
}

const C = {
  orange: "#C4510A", orangeLight: "#FAECE7",
  teal: "#0F6E56", tealLight: "#E1F5EE", tealMid: "#5DCAA5",
  walnut: "#5C3D2E", walnutLight: "#F5EDE8",
  navy: "#0C2340", cream: "#FAF8F5",
};

const QUESTIONS = [
  {
    id: "userRole",
    text: "본인의 주 포지션은 무엇인가요?\nWhat is your primary role?",
    sub: "가장 가까운 것을 선택해주세요 / Choose the closest one",
    multi: false,
    isRoleQuestion: true,
    options: [
      { ko: "프로듀서 (트랙 메이킹 중심)", en: "Producer (track-making focused)", role: "producer", scores: { sound: 2 } },
      { ko: "탑라이너 / 작사가 (멜로디·가사 중심)", en: "Topliner / Lyricist (melody & lyrics focused)", role: "topliner", scores: { lyric: 2, guide: 1 } },
      { ko: "세션 뮤지션 (악기 연주 중심)", en: "Session Musician (instrument focused)", role: "session", scores: { session: 2 } },
      { ko: "아직 잘 모르겠어요 / 올라운더", en: "Not sure / All-rounder", role: "allround", scores: { allround: 1 } },
    ]
  },
  { id:"major", text:"실용음악을 전공했나요?\nDid you study music formally?", sub:"", multi:false, options:[
    { ko:"네, 보컬 전공", en:"Yes — Vocals", scores:{ guide:4, vocalTrained:3, background:2 } },
    { ko:"네, 작곡/프로듀싱 전공", en:"Yes — Composition / Production", scores:{ sound:3, harmony:2, background:2 } },
    { ko:"네, 건반/기타/베이스/드럼 등 실기 전공", en:"Yes — Instrument performance", scores:{ session:4, harmony:1, background:2 } },
    { ko:"실용음악 외 음악 전공 (클래식 등)", en:"Other music degree", scores:{ harmony:3, background:1 } },
    { ko:"비전공 / 독학", en:"Self-taught / No degree", scores:{ indie:1 } },
  ]},
  { id:"instrument", text:"주로 다루는 악기를 모두 선택해주세요\nSelect all instruments you play", sub:"세션으로 기여할 수 있는 수준 / Session-level proficiency", multi:true, options:[
    { ko:"건반 (피아노/신스)", en:"Keys (piano/synth)", scores:{ session:2, harmony:1 } },
    { ko:"기타", en:"Guitar", scores:{ session:2 } },
    { ko:"베이스", en:"Bass", scores:{ session:2 } },
    { ko:"드럼 / 퍼커션", en:"Drums / Percussion", scores:{ session:2 } },
    { ko:"관악기", en:"Wind instrument", scores:{ session:2, indie:1 } },
    { ko:"현악기", en:"String instrument", scores:{ session:2, indie:1 } },
    { ko:"DAW가 내 악기", en:"DAW is my instrument", scores:{ sound:2 } },
    { ko:"딱히 없음", en:"None in particular", scores:{} },
  ]},
  { id:"harmony", text:"화성학 레벨은 어느 정도인가요?\nWhat's your music theory level?", sub:"", multi:false, options:[
    { ko:"코드네임 읽고 기본 진행은 이해해요", en:"Basic chord names and progressions", scores:{ harmony:1 } },
    { ko:"텐션 코드, 전조, 대리코드까지 이해해요", en:"Tension chords, modulation, substitution", scores:{ harmony:3 } },
    { ko:"모드, 폴리코드, 비기능 화성까지 자유롭게 써요", en:"Modal harmony, polychords — freely", scores:{ harmony:5, indie:1 } },
    { ko:"코드보다 느낌으로 작업해요", en:"I work more by feel than theory", scores:{ indie:1, sound:1 } },
    { ko:"화성학은 잘 몰라요", en:"Not familiar with music theory", scores:{} },
  ]},
  { id:"daw", text:"DAW로 트랙을 직접 만들 수 있나요?\nCan you produce a track in a DAW?", sub:"", multi:false, options:[
    { ko:"네, 비트부터 편곡까지 혼자 다 해요", en:"Yes — beat to full arrangement, solo", scores:{ sound:4 } },
    { ko:"어느 정도 가능하지만 믹싱은 약해요", en:"Somewhat, but mixing is weak", scores:{ sound:2 } },
    { ko:"루프/샘플 조합 정도는 해요", en:"Loop/sample level only", scores:{ sound:1 } },
    { ko:"DAW는 잘 못 다뤄요", en:"Not really a DAW person", scores:{} },
  ]},
  { id:"mixing", text:"믹싱과 마스터링을 직접 하나요?\nDo you handle mixing and mastering yourself?", sub:"", multi:false, options:[
    { ko:"네, 플러그인도 꽤 파고드는 편이에요", en:"Yes — and I go deep on plugins", scores:{ sound:4, pluginNerd:3 } },
    { ko:"어느 정도는 해요, 완벽하진 않지만", en:"Somewhat — not perfectly", scores:{ sound:2, pluginNerd:1 } },
    { ko:"믹싱은 남한테 맡기는 편이에요", en:"I usually leave it to someone else", scores:{} },
    { ko:"믹싱이 뭔지는 알지만 안 해봤어요", en:"I know what it is but haven't done it", scores:{} },
  ]},
  { id:"genre", text:"주로 작업하는 장르는 어느 쪽인가요?\nWhat genre do you mainly work in?", sub:"", multi:false, options:[
    { ko:"댄스 / 팝 / 케이팝 메인스트림", en:"Dance / Pop / K-pop mainstream", scores:{ trend:3 } },
    { ko:"발라드 / 미드템포 / R&B", en:"Ballad / Mid-tempo / R&B", scores:{ lyric:1, guide:1 } },
    { ko:"힙합 / 트랩 / 어반", en:"Hip-hop / Trap / Urban", scores:{ lyric:2, sound:1 } },
    { ko:"인디 / 얼터너티브 / 실험적", en:"Indie / Alternative / Experimental", scores:{ indie:3 } },
    { ko:"장르 불문 — 의뢰 오면 다 해요", en:"Genre-agnostic", scores:{ allround:2, trend:1 } },
  ]},
  { id:"lyrics_adopted", text:"작사 시안을 혼자 완성해서 채택된 경험이 있나요?\nHave you written a lyric draft solo and had it accepted?", sub:"기획사 납품, 아티스트 채택, 광고 싱크 등 모두 포함", multi:false, options:[
    { ko:"있음 — 단독으로 완성해서 채택됐어요", en:"Yes — written solo and fully accepted", scores:{ lyric:4 } },
    { ko:"부분 채택 — 공동 수정 후 채택됐어요", en:"Partial — accepted after co-revision", scores:{ lyric:2 } },
    { ko:"제출은 했지만 채택은 아직 없어요", en:"Submitted but not yet accepted", scores:{ lyric:1 } },
    { ko:"없음 — 아직 시도해본 적 없어요", en:"None — haven't tried yet", scores:{} },
  ]},
  { id:"lyrics_speed", text:"송캠프 환경에서 훅 + 벌스 가사를 완성하는 데 보통 얼마나 걸리나요?\nIn a song camp, how long to finish hook + verse lyrics?", sub:"테마나 키워드가 주어졌을 때 / Given a theme or keyword", multi:false, options:[
    { ko:"1시간 이내", en:"Under 1 hour", scores:{ lyric:4 } },
    { ko:"1~3시간", en:"1–3 hours", scores:{ lyric:3 } },
    { ko:"반나절 (3~6시간)", en:"Half a day (3–6 hours)", scores:{ lyric:2 } },
    { ko:"하루 이상", en:"More than a day", scores:{ lyric:1 } },
    { ko:"작사는 잘 안 해봤어요", en:"Haven't really done lyric writing", scores:{} },
  ]},
  { id:"melody", text:"멜로디가 떠오르면 어떻게 표현하나요?\nWhen a melody comes to you, how do you express it?", sub:"", multi:false, options:[
    { ko:"바로 흥얼거리거나 녹음해요", en:"I hum or record it immediately", scores:{ guide:3 } },
    { ko:"건반이나 기타로 먼저 잡아봐요", en:"Work it out on keys or guitar", scores:{ guide:2, session:1 } },
    { ko:"DAW에 MIDI로 먼저 그려요", en:"Sketch it in MIDI on a DAW", scores:{ sound:2, guide:1 } },
    { ko:"머릿속에 있는데 표현이 잘 안 돼요", en:"It's in my head but hard to express", scores:{ lyric:1 } },
    { ko:"멜로디보다는 사운드/가사 중심이에요", en:"I'm more sound/lyrics than melody", scores:{} },
  ]},
  { id:"pitch", text:"오토튠 없을 때 본인 음정 정확도를 솔직하게 평가하면?\nHonestly rate your pitch accuracy — no Auto-Tune", sub:"", multi:false, options:[
    { ko:"오토튠이 필요 없는 수준이에요", en:"Auto-Tune? I don't need it", scores:{ guide:4, pitchClean:3 } },
    { ko:"약간 보정하면 충분해요", en:"Minor correction and I'm fine", scores:{ guide:2, pitchClean:1 } },
    { ko:"보정이 꽤 필요한 편이에요", en:"Needs noticeable correction", scores:{ guide:1 } },
    { ko:"저는 보컬보다 프로덕션/가사 담당이에요", en:"I'm production/lyrics — not a vocalist", scores:{ sound:1, lyric:1 } },
  ]},
  { id:"vocal_range", text:"보컬 레인지는 어느 쪽인가요?\nWhat's your vocal range?", sub:"탑라이너/보컬 지원자만 해당 / For topliners & vocalists", multi:false, options:[
    { ko:"소프라노 / 메조소프라노", en:"Soprano / Mezzo-soprano", scores:{ guide:1 }, range:"soprano" },
    { ko:"알토", en:"Alto", scores:{ guide:1 }, range:"alto" },
    { ko:"테너", en:"Tenor", scores:{ guide:1 }, range:"tenor" },
    { ko:"바리톤 / 베이스", en:"Baritone / Bass", scores:{ guide:1 }, range:"baritone" },
    { ko:"보컬리스트 아님 (프로듀서/작사가)", en:"Not a vocalist", scores:{}, range:"none" },
  ]},
  { id:"idol_exp", text:"가수 또는 연습생 경험이 있나요?\nHave you trained or performed as a singer or trainee?", sub:"", multi:false, options:[
    { ko:"네, 기획사 연습생 경험 있어요", en:"Yes — trained at an agency", scores:{ idol:4, guide:2, vocalTrained:2 } },
    { ko:"네, 독립 아티스트 또는 밴드 활동을 했어요", en:"Yes — independent artist or band", scores:{ idol:2, guide:2, indie:1 } },
    { ko:"공식 활동은 없지만 보컬 트레이닝을 받았어요", en:"Vocal training, no official activity", scores:{ guide:2, vocalTrained:1 } },
    { ko:"없어요 — 저는 만드는 사람이에요", en:"No — I'm a maker, not a performer", scores:{ sound:1, lyric:1 } },
  ]},
  { id:"ear", text:"새 곡을 처음 들었을 때 가장 먼저 뭐가 귀에 들려요?\nFirst thing you notice when hearing a new song?", sub:"", multi:false, options:[
    { ko:"훅 — 꽂히냐 아니냐로 바로 판단해요", en:"The hook — does it grab me or not", scores:{ trend:3, guide:1 } },
    { ko:"사운드와 프로덕션 퀄리티", en:"Sound and production quality", scores:{ sound:2, pluginNerd:1 } },
    { ko:"가사와 스토리라인", en:"The lyrics and storyline", scores:{ lyric:2 } },
    { ko:"전체 분위기와 무드", en:"Overall vibe and mood", scores:{ trend:1, allround:1 } },
    { ko:"코드 진행과 화성 구조", en:"Chord progression and harmonic structure", scores:{ harmony:2 } },
  ]},
  { id:"kpop_knowledge", text:"현재 케이팝 씬을 얼마나 팔로우하나요?\nHow closely do you follow the K-pop scene?", sub:"", multi:false, options:[
    { ko:"차트, 컴백, 기획사 동향까지 실시간으로 알아요", en:"Charts, comebacks, agency moves — real-time", scores:{ trend:4 } },
    { ko:"주요 그룹은 알고 트렌드도 어느정도 파악해요", en:"Know major groups and general trends", scores:{ trend:2 } },
    { ko:"좋아하는 아티스트 위주로만 봐요", en:"Only artists I personally like", scores:{ trend:1 } },
    { ko:"케이팝보다는 제 장르에 집중해요", en:"I focus on my own genre", scores:{ indie:1 } },
  ]},
  { id:"collab", text:"세션 도중 트랙커가 '이 부분 코드 바꿔볼까요?' 하면?\nProducer says 'Let's try a different chord' — your reaction?", sub:"가장 솔직한 반응으로", multi:false, options:[
    { ko:"바로 대안 코드 2~3개 제시해요", en:"I suggest 2–3 alternatives immediately", scores:{ harmony:2, allround:1 } },
    { ko:"어떤 방향인지 물어보고 따라가요", en:"I ask for direction and follow", scores:{ allround:2 } },
    { ko:"잘 모르겠지만 느낌으로 한마디 해요", en:"Not sure technically, but I'll say something", scores:{ trend:1 } },
    { ko:"나는 가사/멜로디 담당이라 패스해요", en:"Not my lane — lyrics/melody side", scores:{ lyric:1, guide:1 } },
    { ko:"새 플러그인 써보자고 제안해요", en:"I suggest trying a new plugin instead", scores:{ pluginNerd:2 } },
  ]},
  { id:"language", text:"주로 작업하는 언어 외 언어 레벨은?\nWhat's your level in a second working language?", sub:"한국 작가 → 영어 / 외국 작가 → 한국어", multi:false, options:[
    { ko:"가사 작업 및 소통 모두 가능한 수준", en:"Comfortable for lyrics and communication", scores:{ global:4 } },
    { ko:"소통은 가능하지만 가사 작업은 어려워요", en:"Can communicate but not for lyrics", scores:{ global:2 } },
    { ko:"기초 수준이에요", en:"Basic level only", scores:{ global:1 } },
    { ko:"거의 못 해요 — 모국어 올인", en:"Barely — all-in on native language", scores:{} },
  ]},
  { id:"published", text:"외부에 공개하거나 납품한 완성곡이 있나요?\nHave you released or placed any finished songs?", sub:"스트리밍, 기획사 납품, 광고 싱크 등 모두 포함", multi:false, options:[
    { ko:"네, 여러 곡 (상업적 경험 포함)", en:"Yes — multiple, including commercial", scores:{ allround:2, trend:1 } },
    { ko:"한두 곡 있어요", en:"One or two", scores:{ allround:1 } },
    { ko:"완성은 했는데 공개는 안 했어요", en:"Finished but unreleased", scores:{ indie:1 } },
    { ko:"아직 없어요", en:"Not yet", scores:{} },
  ]},
  { id:"collab_style", text:"협업할 때 본인의 스타일은?\nHow would you describe your collaboration style?", sub:"", multi:false, isQualitative:true, options:[
    { ko:"리드하는 편이에요 — 방향을 먼저 제시해요", en:"I lead — I set the direction", scores:{ allround:1 }, qual:"leader" },
    { ko:"따라가는 편이에요 — 주어진 방향에 집중해요", en:"I follow — I focus on given direction", scores:{}, qual:"follower" },
    { ko:"핑퐁 스타일 — 아이디어를 주고받아요", en:"Ping-pong — I bounce ideas back and forth", scores:{ allround:1, trend:1 }, qual:"collaborative" },
    { ko:"상황에 따라 달라요", en:"Depends on the situation", scores:{ allround:1 }, qual:"flexible" },
  ]},
  { id:"work_env", text:"가장 잘 만들어지는 환경은?\nWhat environment brings out your best work?", sub:"", multi:false, isQualitative:true, options:[
    { ko:"혼자 집에서 — 조용한 환경이 최고예요", en:"Alone at home — quiet is best", scores:{ indie:1 }, qual:"solo" },
    { ko:"스튜디오에서 팀과 함께", en:"In studio with a team", scores:{ allround:1 }, qual:"team" },
    { ko:"카페나 열린 공간", en:"Café or open space", scores:{ trend:1 }, qual:"open" },
    { ko:"어디서든 상관없어요", en:"Anywhere works for me", scores:{ allround:1 }, qual:"anywhere" },
  ]},
  { id:"camp_expect", text:"송캠프에서 가장 기대하는 것은?\nWhat do you look forward to most in a song camp?", sub:"", multi:false, isQualitative:true, options:[
    { ko:"완성도 높은 곡 한 개", en:"One highly polished song", scores:{ background:1 }, qual:"quality" },
    { ko:"다양한 사람과의 네트워킹", en:"Networking with diverse people", scores:{ trend:1, global:1 }, qual:"network" },
    { ko:"새로운 장르·스타일 시도", en:"Trying new genres and styles", scores:{ indie:1 }, qual:"experiment" },
    { ko:"최대한 많은 곡 작업", en:"Working on as many songs as possible", scores:{ allround:1 }, qual:"quantity" },
  ]},
  { id:"reference", text:"본인 음악의 레퍼런스 아티스트는?\nWho are your musical references?", sub:"가장 가까운 것 하나만 / Choose the closest one", multi:false, isQualitative:true, options:[
    { ko:"케이팝 메이저 (BTS, BLACKPINK, aespa 등)", en:"K-pop major (BTS, BLACKPINK, aespa etc.)", scores:{ trend:2 }, qual:"kpop_major" },
    { ko:"케이팝 인디/하이브리드 (혁오, 실리카겔 등)", en:"K-pop indie/hybrid (Hyukoh, Silica Gel etc.)", scores:{ indie:2 }, qual:"kpop_indie" },
    { ko:"글로벌 팝 (The Weeknd, Billie Eilish 등)", en:"Global pop (The Weeknd, Billie Eilish etc.)", scores:{ trend:1, global:2 }, qual:"global_pop" },
    { ko:"장르 혼합 / 실험적", en:"Genre-blending / Experimental", scores:{ indie:2, harmony:1 }, qual:"experimental" },
  ]},
  { id:"mood", text:"자신있는 무드를 선택해주세요\nChoose your confident mood", sub:"곡을 만들거나 퍼포밍할 때 가장 잘 어울리는 분위기", multi:false, isQualitative:true, options:[
    { ko:"청량한 신인 바이브 ☀️", en:"Fresh & bright newcomer vibe ☀️", scores:{ trend:1 }, qual:"bright" },
    { ko:"다크한 몽환 섹시 🌙", en:"Dark, dreamy & sexy 🌙", scores:{ indie:1 }, qual:"dark" },
  ]},
  { id:"style", text:"가창 스타일은 어느 쪽인가요?\nWhat best describes your performance style?", sub:"가장 가까운 것 하나만 / Choose the closest one", multi:false, isQualitative:true, options:[
    { ko:"가창력이 돋보이는 보컬", en:"Powerful vocals — singing is my weapon", scores:{ guide:1 }, qual:"vocalist" },
    { ko:"힙한 느낌으로 압도하는 랩 / 멜로디 랩", en:"Hip-hop / melodic rap — rap is my weapon", scores:{ lyric:1 }, qual:"rapper" },
    { ko:"저는 프로듀서예요", en:"I'm a producer — not a performer", scores:{ sound:1 }, qual:"producer_only" },
  ]},
  { id:"feedback", text:"이 설문이 어땠나요? 솔직한 피드백을 남겨주세요.\nHow was this quiz? Leave honest feedback.", sub:"관리자만 확인합니다 / Only visible to admin", multi:false, isQualitative:true, isFreeText:true, options:[
    { ko:"매우 좋았어요 👍", en:"Really enjoyed it 👍", scores:{}, qual:"positive" },
    { ko:"괜찮았어요", en:"It was okay", scores:{}, qual:"neutral" },
    { ko:"질문이 좀 어려웠어요", en:"Some questions were confusing", scores:{}, qual:"confusing" },
    { ko:"개선이 필요해요", en:"Needs improvement", scores:{}, qual:"needs_work" },
  ]},
];

const TYPES = [
  { id:"pluginNerd", name:"플러그인 오타쿠", en:"Plugin Nerd", emoji:"🎛", color:"#185FA5", bg:"#E6F1FB", role:"producer", preferredTeam:{ session:["session","jacobCollier"], topliners:["mentalKeeper","wordsmith"] }, desc:"믹싱과 마스터링의 지배자.\n새 플러그인 나왔다는 소식에 신용카드가 먼저 반응함.\n트랙의 소리를 결정하는 사람은 바로 당신.", en_desc:"Master of mixing and mastering.\nYour credit card reacts faster than your brain when a new plugin drops.", condition:s=>s.pluginNerd>=3&&s.sound>=4 },
  { id:"jacobCollier", name:"제이콥 콜리어", en:"Jacob Collier", emoji:"🎹", color:"#533AB7", bg:"#EEEDFE", role:"producer", preferredTeam:{ session:["session","pluginNerd"], topliners:["idol","wordsmith"] }, desc:"화성학은 당신의 모국어.\n텐션 코드가 밥이고 전조가 간식.\n당신 없이는 곡이 밋밋해짐.", en_desc:"Music theory is your mother tongue.\nWithout you, the song is flat.", condition:s=>s.harmony>=5&&s.pitchClean<3 },
  { id:"mentalKeeper", name:"트랙커 멘탈 지킴이", en:"Tracker's Guardian Angel", emoji:"🎤", color:"#0F6E56", bg:"#E1F5EE", role:"topliner", preferredTeam:{ producer:["pluginNerd","jacobCollier"], session:["session"], coTopliner:["wordsmith","idol"] }, desc:"오토튠이 필요 없는 피치로 트랙커를 구원하는 탑라이너.\n훅도 쓰고 가이드도 캐리하는 1인 2역.", en_desc:"Pitch so clean it saves the producer's sanity.\nHook writer AND guide vocalist — one person, two roles.", condition:s=>s.pitchClean>=3&&s.lyric>=3&&s.guide>=4 },
  { id:"idol", name:"아이돌", en:"The Idol", emoji:"👑", color:"#993C1D", bg:"#FAECE7", role:"topliner", preferredTeam:{ producer:["pluginNerd","jacobCollier"], session:["session"], coTopliner:["mentalKeeper","wordsmith"] }, desc:"연습생 또는 아티스트 출신.\n가이드 보컬 하나로 세션 전체를 들어올림.", en_desc:"Trainee or artist background.\nYour guide vocal lifts the entire session.", condition:s=>s.idol>=3&&s.guide>=3 },
  { id:"indie", name:"인디의 개천에서 난 용", en:"Indie Dragon", emoji:"🐉", color:"#3B6D11", bg:"#EAF3DE", role:"producer", preferredTeam:{ session:["session","pluginNerd"], topliners:["mentalKeeper","global"] }, desc:"독특한 색깔로 언더그라운드에서 케이팝까지 진출하는 초월자.\n당신의 이상함이 곧 무기.", en_desc:"From underground to K-pop on the strength of your unique vision.\nYour weirdness is your weapon.", condition:s=>s.indie>=4 },
  { id:"wordsmith", name:"야마 있는 작사가", en:"The Wordsmith", emoji:"✍️", color:"#854F0B", bg:"#FAEEDA", role:"topliner", preferredTeam:{ producer:["pluginNerd","jacobCollier"], session:["session"], coTopliner:["mentalKeeper","idol"] }, desc:"키워드 하나면 충분.\n가이드는 AI한테 맡기고 당신은 언어로 승부함.", en_desc:"Give me a keyword and I'll give you a song.\nLet the AI handle the guide — you handle the words.", condition:s=>s.lyric>=4&&s.guide<3 },
  { id:"plugger", name:"Song Plugger", en:"Song Plugger", emoji:"🐝", color:"#3C3489", bg:"#EEEDFE", role:"topliner", preferredTeam:{ producer:["pluginNerd"], session:["session"], coTopliner:["mentalKeeper","wordsmith"] }, desc:"인맥이 곧 실력.\n케이팝 씬을 꿰고 기획사 문을 두드릴 수 있음.", en_desc:"Your network is your skillset.\nYou know the scene and can knock on agency doors.", condition:s=>s.trend>=5&&s.sound<2&&s.lyric<2 },
  { id:"global", name:"글로벌 카드", en:"Global Card", emoji:"🌏", color:"#0C447C", bg:"#E6F1FB", role:"topliner", preferredTeam:{ producer:["pluginNerd","indie"], session:["session"], coTopliner:["mentalKeeper","wordsmith"] }, desc:"언어 능력으로 팀의 가치를 끌어올리는 작가.\n글로벌 피칭에서 가장 빛나는 유형.", en_desc:"You elevate the team through language.\nThe one who shines most in global pitching.", condition:s=>s.global>=4&&s.lyric>=2&&s.guide>=2 },
  { id:"session", name:"세션 히어로", en:"Session Hero", emoji:"🎸", color:"#5F5E5A", bg:"#F1EFE8", role:"session", preferredTeam:{ producer:["pluginNerd","jacobCollier","indie"], topliners:["mentalKeeper","wordsmith"] }, desc:"악기 하나(혹은 여럿)만큼은 끝내줌.\n기계가 절대 대체할 수 없는 유형.", en_desc:"One instrument — or several — mastered.\nNo machine can replace you.", condition:s=>s.session>=4 },
  { id:"allround", name:"올라운더", en:"All-Rounder", emoji:"🌀", color:"#3C3489", bg:"#EEEDFE", role:"producer", preferredTeam:{ session:["session"], topliners:["mentalKeeper","wordsmith"] }, desc:"뭐든 어느정도는 됨.\n팀의 접착제이자 마지막 보루.", en_desc:"Solid at everything. The glue. The safety net.", condition:()=>true },
];

const AXES_KEYS = ["sound","lyric","guide","trend","harmony"];
const AXES_LABELS = ["사운드","가사","가이드","대중성","화성학"];

function calcScores(answers, multiAnswers) {
  const s = { sound:0,lyric:0,guide:0,trend:0,harmony:0,session:0,idol:0,indie:0,allround:0,pluginNerd:0,global:0,background:0,vocalTrained:0,pitchClean:0 };
  QUESTIONS.forEach((q,qi) => {
    if (q.multi) {
      (multiAnswers[qi]||[]).forEach(i => { Object.entries(q.options[i].scores||{}).forEach(([k,v])=>{ s[k]=(s[k]||0)+v; }); });
    } else {
      const ai = answers[qi];
      if (ai!==null&&ai!==undefined) Object.entries(q.options[ai].scores||{}).forEach(([k,v])=>{ s[k]=(s[k]||0)+v; });
    }
  });
  return s;
}

function assignType(scores, userRole) {
  let bestType = TYPES[TYPES.length - 1];
  for (const t of TYPES.slice(0, -1)) { if (t.condition(scores)) { bestType = t; break; } }
  const typeRole = bestType.role;
  let recommendedPosition = null, roleMismatch = false;
  if (userRole && userRole !== "allround" && typeRole !== userRole) {
    roleMismatch = true;
    const ps = { producer:(scores.sound||0)+(scores.harmony||0)+(scores.pluginNerd||0), topliner:(scores.lyric||0)+(scores.guide||0)+(scores.trend||0), session:(scores.session||0)*2 };
    recommendedPosition = Object.entries(ps).sort((a,b)=>b[1]-a[1])[0][0];
  }
  return { type: bestType, roleMismatch, recommendedPosition, userRole };
}

function getVocalRange(answers) {
  const qi = QUESTIONS.findIndex(q=>q.id==="vocal_range");
  if (qi<0||answers[qi]===null) return "none";
  return QUESTIONS[qi].options[answers[qi]].range||"none";
}

const GENRE_LABELS = ["댄스/팝","발라드/R&B","힙합/어반","인디","올라운더"];
const GENRE_COLORS = {"댄스/팝":"#185FA5","발라드/R&B":"#993C1D","힙합/어반":"#3C3489","인디":"#3B6D11","올라운더":"#5F5E5A"};

function getGenreLabel(p) {
  const gi = QUESTIONS.findIndex(q=>q.id==="genre");
  if (gi<0||!p.answers||p.answers[gi]===null||p.answers[gi]===undefined) return null;
  return GENRE_LABELS[p.answers[gi]]||null;
}

function getMoodLabel(p) {
  const qi = QUESTIONS.findIndex(q=>q.id==="mood");
  if (qi<0||!p.answers||p.answers[qi]===null||p.answers[qi]===undefined) return null;
  return ["bright","dark"][p.answers[qi]]||null;
}

function getStyleLabel(p) {
  const qi = QUESTIONS.findIndex(q=>q.id==="style");
  if (qi<0||!p.answers||p.answers[qi]===null||p.answers[qi]===undefined) return null;
  return ["vocalist","rapper","producer_only"][p.answers[qi]]||null;
}

function findAllTeams(participants, brief, teamCount = TEAM_COUNT, teamSize = 4) {
  const totalScore = p => AXES_KEYS.reduce((s,k)=>s+(p.scores?.[k]||0),0);
  const genreMatchMap = { dance:"댄스/팝", ballad:"발라드/R&B", hiphop:"힙합/어반", indie:"인디" };
  const targetGenre = brief.genre!=="any" ? genreMatchMap[brief.genre] : null;
  const fitScore = p => {
    let s = totalScore(p);
    const g = getGenreLabel(p);
    if (targetGenre) { if (g===targetGenre) s+=20; else if (g==="올라운더") s+=10; }
    return s;
  };
  // 올라운더: lyric+guide 합이 4 이상이면 탑라이너 풀로, 미만이면 프로듀서 풀로 분류
  const tlScore = p => (p.scores?.lyric||0)+(p.scores?.guide||0);
  const allrounders = participants.filter(p=>p.typeId==="allround");
  const allroundAsTl = allrounders.filter(p=>tlScore(p)>=4);
  const allroundAsProd = allrounders.filter(p=>tlScore(p)<4);
  const baseByRole = role => participants
    .filter(p=>{ const t=TYPES.find(x=>x.id===p.typeId); return t?.role===role && p.typeId!=="allround"; })
    .sort((a,b)=>fitScore(b)-fitScore(a));
  const producers = [...baseByRole("producer"),...allroundAsProd].sort((a,b)=>fitScore(b)-fitScore(a));
  const sessions = baseByRole("session");
  let topliners = [...baseByRole("topliner"),...allroundAsTl].sort((a,b)=>fitScore(b)-fitScore(a));
  if (brief.vocalRange&&brief.vocalRange!=="any") {
    topliners=[...topliners.filter(p=>p.vocalRange===brief.vocalRange),...topliners.filter(p=>p.vocalRange!==brief.vocalRange)];
  }
  if (brief.language==="bilingual") {
    topliners=[...topliners.filter(p=>(p.scores?.global||0)>=2),...topliners.filter(p=>(p.scores?.global||0)<2)];
  }
  // 무드 필터: bright/dark 우선 배치
  if (brief.mood&&brief.mood!=="any") {
    topliners=[...topliners.filter(p=>getMoodLabel(p)===brief.mood),...topliners.filter(p=>getMoodLabel(p)!==brief.mood)];
  }
  // 스타일 비중: balanced이면 보컬/랩 교대 배치, 우선순위면 해당 스타일 앞으로
  if (brief.style&&brief.style!=="any") {
    if (brief.style==="balanced") {
      const vocalists=topliners.filter(p=>getStyleLabel(p)==="vocalist");
      const rappers=topliners.filter(p=>getStyleLabel(p)==="rapper");
      const others=topliners.filter(p=>!["vocalist","rapper"].includes(getStyleLabel(p)));
      const interleaved=[];
      const maxLen=Math.max(vocalists.length,rappers.length);
      for(let i=0;i<maxLen;i++){ if(i<vocalists.length)interleaved.push(vocalists[i]); if(i<rappers.length)interleaved.push(rappers[i]); }
      topliners=[...interleaved,...others];
    } else {
      topliners=[...topliners.filter(p=>getStyleLabel(p)===brief.style),...topliners.filter(p=>getStyleLabel(p)!==brief.style)];
    }
  }
  // 참가자 수 기반으로 실제 활성 팀 수 계산: 팀 인원수에 맞춰 최소한의 팀만 활성화
  const effectiveTeamCount = Math.min(teamCount, Math.max(1, Math.ceil(participants.length / teamSize)));

  const teams = Array.from({ length: teamCount }, (_,i) => ({ id:i+1, slots:{ 0:null,1:null,2:null,3:null } }));
  producers.slice(0, effectiveTeamCount).forEach((p,i)=>{ teams[i].slots[0]=p; });

  if (teamSize===4) {
    // 실제 세션 플레이어 먼저 배정
    sessions.slice(0, effectiveTeamCount).forEach((p,i)=>{ teams[i].slots[1]=p; });

    // 세션 슬롯이 비어있는 팀: 미배정 프로듀서 중 session 점수 있는 사람으로 채우기
    const assignedEmails = new Set(
      teams.flatMap(t => Object.values(t.slots).filter(Boolean).map(p => p.email))
    );
    const flexSessions = producers
      .filter(p => !assignedEmails.has(p.email))
      .sort((a,b) => (b.scores?.session||0) - (a.scores?.session||0));
    teams.slice(0, effectiveTeamCount).forEach(team => {
      if (!team.slots[1] && flexSessions.length > 0) {
        const flex = flexSessions.shift();
        team.slots[1] = flex;
        assignedEmails.add(flex.email);
      }
    });
  }

  // 탑라이너: 스네이크 드래프트로 고른 분배 (미배정자만)
  const assignedAfterSession = new Set(
    teams.flatMap(t => Object.values(t.slots).filter(Boolean).map(p => p.email))
  );
  const availableTopliners = topliners.filter(p => !assignedAfterSession.has(p.email));
  availableTopliners.slice(0, effectiveTeamCount).forEach((p,i)=>{ teams[i].slots[2]=p; });
  availableTopliners.slice(effectiveTeamCount, effectiveTeamCount*2).forEach((p,i)=>{ teams[effectiveTeamCount-1-i].slots[3]=p; });
  return teams;
}

function maskName(nick) { return nick.charAt(0).toUpperCase()+"***"; }

function RadarChart({ scores }) {
  const ref = useRef(null), chartRef = useRef(null);
  useEffect(() => {
    if (!ref.current||typeof Chart==="undefined") return;
    if (chartRef.current) chartRef.current.destroy();
    // eslint-disable-next-line no-undef
    chartRef.current = new Chart(ref.current.getContext("2d"), {
      type:"radar",
      data:{ labels:["사운드\nSound","가사\nLyric","가이드\nGuide","대중성\nTrend","화성학\nHarmony"], datasets:[{ data:AXES_KEYS.map(k=>Math.min(8,scores[k]||0)), backgroundColor:"rgba(196,81,10,0.12)", borderColor:C.orange, borderWidth:2, pointBackgroundColor:C.orange, pointRadius:4 }] },
      options:{ responsive:false, layout:{ padding:{ left:28, right:28, top:16, bottom:16 } }, plugins:{ legend:{ display:false } }, scales:{ r:{ min:0, max:8, ticks:{ display:false }, grid:{ color:"rgba(92,61,46,0.15)" }, pointLabels:{ font:{ size:10 }, padding:6 } } } }
    });
    return ()=>{ if(chartRef.current) chartRef.current.destroy(); };
  }, [scores]);
  return <canvas ref={ref} width={280} height={280} role="img" aria-label="Radar chart"/>;
}

function CreditFooter() {
  return (
    <div style={{ marginTop:"2.5rem", borderTop:`1px solid rgba(92,61,46,0.12)`, paddingTop:"1.5rem" }}>
      <div style={{ background:C.cream, border:`0.5px solid rgba(92,61,46,0.12)`, borderRadius:12, padding:"1.25rem" }}>
        <div style={{ fontSize:13, fontWeight:600, color:C.orange, letterSpacing:1.5, marginBottom:4 }}>STUDIO BORAN</div>
        <div style={{ fontSize:11, color:C.walnut, letterSpacing:1, marginBottom:12 }}>by THAIDAI · Pattaya, Thailand</div>
        <div style={{ fontSize:12, color:C.navy, lineHeight:1.7, marginBottom:14 }}>파타야 3층, 석양이 내려다보이는 미드센츄리 모던 레코딩 스튜디오.<br/>장비보다 영감이 앞서는 곳 — K-pop Song Camp & Creative Residency.</div>
        <div style={{ display:"flex", gap:16, flexWrap:"wrap" }}>
          <a href="https://instagram.com/2nner_child" target="_blank" rel="noreferrer" style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, color:C.orange, textDecoration:"none" }}><span>📷</span> @2nner_child</a>
          <a href="mailto:2nnerchild@naver.com" style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, color:C.orange, textDecoration:"none" }}><span>✉️</span> 2nnerchild@naver.com</a>
        </div>
        <div style={{ fontSize:10, color:C.walnut, marginTop:12, paddingTop:12, borderTop:`0.5px solid rgba(92,61,46,0.1)`, fontWeight:600 }}>
          © 2026 Studio Boran — Proprietary Software<br/>
          <span style={{ opacity:0.7, fontWeight:400 }}>Unauthorized copying or redistribution is prohibited. See LICENSE.md</span>
        </div>
      </div>
    </div>
  );
}

function ParticipantModal({ participant, onClose }) {
  const t = TYPES.find(x=>x.id===participant.typeId);
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.45)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", padding:"1rem" }} onClick={onClose}>
      <div style={{ background:"white", borderRadius:16, padding:"1.5rem", maxWidth:460, width:"100%", maxHeight:"90vh", overflowY:"auto" }} onClick={e=>e.stopPropagation()}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"1rem" }}>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <div style={{ width:40, height:40, borderRadius:"50%", background:t?.bg||"#eee", display:"flex", alignItems:"center", justifyContent:"center", fontSize:20 }}>{t?.emoji}</div>
            <div>
              <div style={{ fontWeight:500, fontSize:15, color:C.navy }}>{participant.nick}</div>
              <div style={{ fontSize:12, color:C.walnut }}>{t?.name} · {participant.email}</div>
            </div>
          </div>
          <button onClick={onClose} style={{ background:"none", border:"none", fontSize:22, cursor:"pointer", color:C.walnut, lineHeight:1 }}>×</button>
        </div>
        <div style={{ display:"flex", justifyContent:"center", marginBottom:"1rem" }}>
          <RadarChart scores={participant.scores||{}} />
        </div>
        <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:"1rem" }}>
          {AXES_KEYS.map((k,i)=>(
            <div key={k} style={{ background:C.cream, borderRadius:6, padding:"4px 10px", fontSize:12 }}>
              <strong style={{ color:C.navy }}>{Math.min(8,participant.scores?.[k]||0)}</strong>
              <span style={{ color:C.walnut, marginLeft:4 }}>{AXES_LABELS[i]}</span>
            </div>
          ))}
        </div>
        <div style={{ borderTop:`0.5px solid rgba(92,61,46,0.12)`, paddingTop:"1rem", fontSize:12, color:C.navy, lineHeight:2 }}>
          <div>포지션: {participant.userRole||"-"}</div>
          <div>보컬 레인지: {participant.vocalRange||"-"}</div>
          <div>퍼블리셔: {participant.pub==="yes"?(participant.pubName||"있음"):participant.pub==="unsure"?"불확실":"없음"}</div>
          {(()=>{ const g=getGenreLabel(participant); return g?(<div>주요 장르: <span style={{ color:GENRE_COLORS[g],fontWeight:500 }}>{g}</span></div>):null; })()}
        </div>
        <div style={{ borderTop:`0.5px solid rgba(92,61,46,0.12)`, paddingTop:"1rem", marginTop:"1rem" }}>
  <div style={{ fontSize:12, fontWeight:500, color:C.navy, marginBottom:8 }}>답변 전체 / Full Answers</div>
  {!participant.answers ? (
    <div style={{ fontSize:11, color:C.walnut, background:"rgba(92,61,46,0.04)", padding:"10px 12px", borderRadius:6 }}>
      이 참가자는 답변 데이터가 저장되지 않은 구버전 제출입니다.<br/>
      <span style={{ opacity:0.65 }}>No answer data — submitted before this feature was added.</span>
    </div>
  ) : (
  <div style={{ fontSize:11, color:C.navy, lineHeight:1.8, maxHeight:300, overflowY:"auto", background:"rgba(92,61,46,0.02)", padding:"8px 10px", borderRadius:6 }}>
    {QUESTIONS.map((q,qi)=>{
      const ans = participant.answers[qi];
      const multiAns = participant.multiAnswers?.[qi];
      let ansText = "-";
      if (q.multi && multiAns?.length>0) {
        ansText = multiAns.map(i=>q.options[i]?.ko).join(", ");
      } else if (!q.multi && ans!==null && ans!==undefined) {
        ansText = q.options[ans]?.ko || "-";
      }
      return (
        <div key={qi} style={{ marginBottom:8, paddingBottom:8, borderBottom:`0.5px solid rgba(92,61,46,0.08)` }}>
          <div style={{ fontWeight:500, fontSize:10, color:C.walnut, marginBottom:3 }}>Q{qi+1}. {q.text.split("\n")[0]}</div>
          <div style={{ fontSize:10, color:C.navy }}>{ansText}</div>
        </div>
      );
    })}
  </div>
  )}
</div>
      </div>
    </div>
  );
}

const TEAM_COUNT = 10;
const POSITIONS = ["메인 프로듀서","서브/세션","탑라이너 1","탑라이너 2"];
const POS_COLORS = ["#E6F1FB","#EAF3DE","#FAECE7","#FAEEDA"];
const POS_TEXT = ["#185FA5","#3B6D11","#993C1D","#854F0B"];
const makeEmptyTeams = () => Array.from({ length: TEAM_COUNT }, (_,i) => ({ id:i+1, slots:{ 0:null,1:null,2:null,3:null } }));

function MemberTooltip({ p, x, y }) {
  const t = TYPES.find(x2=>x2.id===p.typeId);
  const g = getGenreLabel(p);
  const mood = getMoodLabel(p);
  const style = getStyleLabel(p);
  const MOOD_LABEL = { bright:"☀️ 청량", dark:"🌙 다크" };
  const STYLE_LABEL = { vocalist:"🎤 보컬", rapper:"🎧 랩", producer_only:"🎛 프로듀서" };
  return (
    <div style={{ position:"fixed", top:y+8, left:x+8, zIndex:9999,
      background:"white", border:`0.5px solid rgba(92,61,46,0.2)`, borderRadius:10, padding:"10px 12px",
      boxShadow:"0 4px 20px rgba(0,0,0,0.18)", whiteSpace:"nowrap", pointerEvents:"none", minWidth:170 }}>
      <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:8 }}>
        <div style={{ width:26, height:26, borderRadius:"50%", background:t?.bg||"#eee", display:"flex", alignItems:"center", justifyContent:"center", fontSize:13 }}>{t?.emoji}</div>
        <div>
          <div style={{ fontSize:11, fontWeight:500, color:C.navy }}>{p.nick}</div>
          <div style={{ fontSize:9, color:C.walnut }}>{t?.name}</div>
        </div>
      </div>
      <div style={{ display:"flex", gap:4, flexWrap:"wrap", marginBottom:8 }}>
        {g&&<span style={{ fontSize:9, color:GENRE_COLORS[g], background:GENRE_COLORS[g]+"18", borderRadius:3, padding:"1px 5px" }}>{g}</span>}
        {mood&&<span style={{ fontSize:9, color:"#5F5E5A", background:"rgba(92,61,46,0.07)", borderRadius:3, padding:"1px 5px" }}>{MOOD_LABEL[mood]}</span>}
        {style&&style!=="producer_only"&&<span style={{ fontSize:9, color:"#5F5E5A", background:"rgba(92,61,46,0.07)", borderRadius:3, padding:"1px 5px" }}>{STYLE_LABEL[style]}</span>}
      </div>
      {AXES_KEYS.map((k,i)=>{
        const v = Math.min(8, p.scores?.[k]||0);
        return (
          <div key={k} style={{ display:"flex", alignItems:"center", gap:6, marginBottom:3 }}>
            <div style={{ fontSize:9, color:C.walnut, width:34, textAlign:"right" }}>{AXES_LABELS[i]}</div>
            <div style={{ flex:1, height:5, background:"rgba(92,61,46,0.08)", borderRadius:3 }}>
              <div style={{ width:`${(v/8)*100}%`, height:"100%", background:C.orange, borderRadius:3 }}/>
            </div>
            <div style={{ fontSize:9, color:C.navy, width:10 }}>{v}</div>
          </div>
        );
      })}
    </div>
  );
}

function TeamBoard({ participants, teams, setTeams, teamSize, onDeleteParticipant, onSelectP, confirmReset, setConfirmReset, resetAllParticipants, lastVisitTs }) {
  const [dragging, setDragging] = useState(null);
  const [hoveredTeam, setHoveredTeam] = useState(null);
  const [hoveredMember, setHoveredMember] = useState(null);
  const touchDragging = useRef(null);
  const ghostEl = useRef(null);

  const activeSlots = teamSize===3 ? [0,2,3] : [0,1,2,3];
  const usedEmails = new Set(teams.flatMap(t=>Object.values(t.slots).filter(Boolean).map(p=>p.email)));
  const bench = participants.filter(p=>!usedEmails.has(p.email));

  // ── 데스크톱 DnD ──
  function onDragStart(e, participant, fromTeam=null, fromSlot=null) {
    setDragging({ participant, fromTeam, fromSlot });
    e.dataTransfer.effectAllowed = "move";
  }
  function onDragOver(e) { e.preventDefault(); }
  function onDrop(e, toTeam, toSlot) {
    e.preventDefault();
    if (!dragging) return;
    const { participant, fromTeam, fromSlot } = dragging;
    setTeams(prev => {
      const next = prev.map(t=>({ ...t, slots:{ ...t.slots } }));
      if (fromTeam!==null&&fromSlot!==null) next[fromTeam-1].slots[fromSlot]=null;
      next[toTeam-1].slots[toSlot] = participant;
      return next;
    });
    setDragging(null);
  }
  function onDropBench(e) {
    e.preventDefault();
    if (!dragging) return;
    const { fromTeam, fromSlot } = dragging;
    if (fromTeam!==null&&fromSlot!==null) {
      setTeams(prev => { const next=prev.map(t=>({...t,slots:{...t.slots}})); next[fromTeam-1].slots[fromSlot]=null; return next; });
    }
    setDragging(null);
  }

  // ── 모바일 터치 DnD ──
  function createGhost(srcEl, cx, cy) {
    const r = srcEl.getBoundingClientRect();
    const g = srcEl.cloneNode(true);
    g.style.cssText = `position:fixed;left:${cx - r.width/2}px;top:${cy - r.height/2}px;width:${r.width}px;opacity:0.85;z-index:9999;pointer-events:none;background:white;border-radius:8px;padding:6px 10px;box-shadow:0 6px 20px rgba(0,0,0,0.25);transform:scale(1.06);transition:none;`;
    document.body.appendChild(g);
    return g;
  }
  function removeGhost() {
    if (ghostEl.current) { ghostEl.current.remove(); ghostEl.current = null; }
  }
  function onTouchStart(e, participant, fromTeam=null, fromSlot=null) {
    touchDragging.current = { participant, fromTeam, fromSlot };
    const t = e.touches[0];
    ghostEl.current = createGhost(e.currentTarget, t.clientX, t.clientY);
  }
  function onTouchMove(e) {
    if (!touchDragging.current || !ghostEl.current) return;
    e.preventDefault();
    const t = e.touches[0];
    const r = ghostEl.current.getBoundingClientRect();
    ghostEl.current.style.left = `${t.clientX - r.width/2}px`;
    ghostEl.current.style.top  = `${t.clientY - r.height/2}px`;
  }
  function onTouchEnd(e) {
    if (!touchDragging.current) return;
    const t = e.changedTouches[0];
    removeGhost();
    // 고스트가 있으면 elementFromPoint가 고스트를 잡으므로 제거 후 탐색
    const el = document.elementFromPoint(t.clientX, t.clientY);
    const dropCell = el?.closest("[data-drop-team]");
    const dropBench = el?.closest("[data-drop-bench]");
    const { participant, fromTeam, fromSlot } = touchDragging.current;
    if (dropBench) {
      if (fromTeam!==null&&fromSlot!==null) {
        setTeams(prev=>{ const next=prev.map(t2=>({...t2,slots:{...t2.slots}})); next[fromTeam-1].slots[fromSlot]=null; return next; });
      }
    } else if (dropCell) {
      const toTeam = parseInt(dropCell.dataset.dropTeam);
      const toSlot = parseInt(dropCell.dataset.dropSlot);
      setTeams(prev=>{
        const next=prev.map(t2=>({...t2,slots:{...t2.slots}}));
        if (fromTeam!==null&&fromSlot!==null) next[fromTeam-1].slots[fromSlot]=null;
        next[toTeam-1].slots[toSlot]=participant;
        return next;
      });
    }
    touchDragging.current = null;
    setDragging(null);
  }

  // touchmove는 passive:false 필요 → document에 직접 등록
  useEffect(() => {
    function handler(e) { if (touchDragging.current) e.preventDefault(); }
    document.addEventListener("touchmove", handler, { passive: false });
    return () => document.removeEventListener("touchmove", handler);
  }, []);

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
        <div style={{ fontSize:13, color:C.walnut }}>팀 번호 tap/hover → 능력치 합산 · 이름 tap/hover → 참가자 카드</div>
        <button onClick={()=>setTeams(makeEmptyTeams())} style={{ fontSize:12, padding:"4px 12px", borderRadius:6, border:`0.5px solid rgba(92,61,46,0.3)`, background:"white", color:C.walnut, cursor:"pointer" }}>초기화</button>
      </div>
      <div style={{ fontSize:12, fontWeight:500, color:C.walnut, marginBottom:6 }}>대기 ({bench.length}명)</div>
      <div data-drop-bench onDrop={onDropBench} onDragOver={onDragOver}
        style={{ minHeight:44, border:`0.5px dashed rgba(92,61,46,0.3)`, borderRadius:8, padding:"6px 8px", display:"flex", flexWrap:"wrap", gap:6, background:"rgba(92,61,46,0.02)", marginBottom:14 }}>
        {bench.length===0&&<div style={{ fontSize:12, color:C.walnut, opacity:0.4, padding:"4px" }}>모두 배치됨</div>}
        {bench.map(p=>{ const t=TYPES.find(x=>x.id===p.typeId); const g=getGenreLabel(p); return (
          <div key={p.email} draggable
            onDragStart={e=>onDragStart(e,p)}
            onTouchStart={e=>onTouchStart(e,p)}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            style={{ display:"flex", alignItems:"center", gap:5, padding:"4px 8px", borderRadius:6, background:"white", border:`0.5px solid rgba(92,61,46,0.2)`, cursor:"grab", fontSize:12, color:C.navy, userSelect:"none", touchAction:"none" }}>
            <span>{t?.emoji}</span><span>{p.nick}</span>
            {g&&<span style={{ fontSize:9, color:GENRE_COLORS[g], background:GENRE_COLORS[g]+"18", borderRadius:3, padding:"0 4px" }}>{g}</span>}
          </div>
        );})}
      </div>
      <div style={{ overflowX:"auto" }}>
        <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
          <thead>
            <tr style={{ background:C.cream }}>
              <th style={{ padding:"8px 10px", textAlign:"left", color:C.walnut, fontWeight:500, border:`0.5px solid rgba(92,61,46,0.15)`, width:60 }}>팀</th>
              {activeSlots.map(si=>(
                <th key={si} style={{ padding:"8px 10px", textAlign:"left", color:POS_TEXT[si], fontWeight:500, border:`0.5px solid rgba(92,61,46,0.15)`, background:POS_COLORS[si], minWidth:110 }}>{POSITIONS[si]}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {teams.map(team=>{
              const members = activeSlots.map(si=>team.slots[si]).filter(Boolean);
              const totals = AXES_KEYS.reduce((acc,k)=>{ acc[k]=members.reduce((s,m)=>s+(m.scores?.[k]||0),0); return acc; },{});
              totals.sum = AXES_KEYS.reduce((s,k)=>s+totals[k],0);
              const bilingualCount = members.filter(m=>(m.scores?.global||0)>=2).length;
              const pubCount = members.filter(m=>m.pub==="yes").length;
              return (
              <tr key={team.id}>
                <td
                  onMouseEnter={()=>setHoveredTeam(team.id)}
                  onMouseLeave={()=>setHoveredTeam(null)}
                  style={{ padding:"6px 8px", textAlign:"center", fontWeight:500, color:C.walnut, border:`0.5px solid rgba(92,61,46,0.15)`, background:C.cream, position:"relative", verticalAlign:"top" }}>
                  <div>{team.id}</div>
                  {bilingualCount>=2&&<div style={{ fontSize:9, color:C.teal, lineHeight:1.5, whiteSpace:"nowrap" }}>소통 원활</div>}
                  {pubCount>=2&&<div style={{ fontSize:9, color:C.teal, lineHeight:1.5, whiteSpace:"nowrap" }}>피칭 활성도 높음</div>}
                  {hoveredTeam===team.id&&members.length>0&&(
                    <div style={{ position:"absolute", zIndex:200, left:"105%", top:0, background:"white", border:`0.5px solid rgba(92,61,46,0.2)`, borderRadius:8, padding:"8px 12px", boxShadow:"0 4px 16px rgba(0,0,0,0.12)", whiteSpace:"nowrap", textAlign:"left" }}>
                      <div style={{ fontSize:10, fontWeight:500, color:C.navy, marginBottom:4 }}>팀 {team.id} 능력치 합산</div>
                      {AXES_KEYS.map((k,i)=>(
                        <div key={k} style={{ fontSize:10, color:C.walnut, lineHeight:1.7 }}>{AXES_LABELS[i]}: <strong style={{ color:C.navy }}>{totals[k]}</strong></div>
                      ))}
                      <div style={{ fontSize:10, fontWeight:500, color:C.orange, borderTop:`0.5px solid rgba(92,61,46,0.1)`, marginTop:4, paddingTop:4 }}>합계: {totals.sum}</div>
                    </div>
                  )}
                </td>
                {activeSlots.map(si=>{ const occ=team.slots[si]; const t2=occ?TYPES.find(x=>x.id===occ.typeId):null; const g=occ?getGenreLabel(occ):null; return (
                  <td key={si}
                    data-drop-team={team.id} data-drop-slot={si}
                    onDrop={e=>onDrop(e,team.id,si)} onDragOver={onDragOver}
                    style={{ padding:"5px 8px", border:`0.5px solid rgba(92,61,46,0.15)`, background:occ?POS_COLORS[si]:"white", minWidth:110 }}>
                    {occ ? (
                      <div draggable
                        onDragStart={e=>onDragStart(e,occ,team.id,si)}
                        onTouchStart={e=>onTouchStart(e,occ,team.id,si)}
                        onTouchMove={onTouchMove}
                        onTouchEnd={onTouchEnd}
                        onMouseEnter={e=>setHoveredMember({p:occ,x:e.clientX,y:e.clientY})}
                        onMouseMove={e=>setHoveredMember(prev=>prev?{...prev,x:e.clientX,y:e.clientY}:null)}
                        onMouseLeave={()=>setHoveredMember(null)}
                        style={{ display:"flex", alignItems:"center", gap:4, cursor:"grab", userSelect:"none", touchAction:"none" }}>
                        <span>{t2?.emoji}</span>
                        <div>
                          <div style={{ color:POS_TEXT[si], fontWeight:500 }}>{occ.nick}</div>
                          {g&&<div style={{ fontSize:9, color:GENRE_COLORS[g] }}>{g}</div>}
                        </div>
                      </div>
                    ) : (
                      <div style={{ color:"rgba(92,61,46,0.2)", fontSize:11 }}>비어있음</div>
                    )}
                  </td>
                );})}
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {hoveredMember&&<MemberTooltip p={hoveredMember.p} x={hoveredMember.x} y={hoveredMember.y}/>}

      {/* 참가자 목록 */}
      <div style={{ marginTop:24, borderTop:`0.5px solid rgba(92,61,46,0.12)`, paddingTop:16 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
          <div style={{ fontWeight:500, fontSize:14, color:C.navy }}>전체 참가자 ({participants.length}명)</div>
          {participants.length>0&&(confirmReset?(
            <div style={{ display:"flex", gap:6, alignItems:"center" }}>
              <span style={{ fontSize:12, color:"#dc2626" }}>정말 삭제할까요?</span>
              <button style={{ background:"#dc2626", border:"none", borderRadius:6, padding:"4px 10px", fontSize:12, color:"white", cursor:"pointer" }} onClick={resetAllParticipants}>전체 삭제</button>
              <button style={{ background:"transparent", border:`0.5px solid rgba(12,35,64,0.25)`, borderRadius:6, padding:"4px 10px", fontSize:12, color:C.navy, cursor:"pointer" }} onClick={()=>setConfirmReset(false)}>취소</button>
            </div>
          ):(
            <button style={{ background:"transparent", border:`0.5px solid rgba(220,38,38,0.4)`, borderRadius:6, padding:"4px 10px", fontSize:12, color:"#dc2626", cursor:"pointer" }} onClick={()=>setConfirmReset(true)}>전체 초기화</button>
          ))}
        </div>
        {participants.length===0&&<div style={{ fontSize:13, color:C.walnut }}>아직 참가자가 없어요</div>}
        {[...participants].sort((a,b)=>(a.ts||0)-(b.ts||0)).map((p,i)=>{
          const t=TYPES.find(x=>x.id===p.typeId);
          const g=getGenreLabel(p);
          const teamOf = teams.find(tm=>Object.values(tm.slots).some(s=>s?.email===p.email));
          const isNew = lastVisitTs && (p.ts||0) > lastVisitTs;
          return (
            <div key={i} style={{ display:"flex", alignItems:"center", gap:10, padding:"8px 0", borderBottom:`0.5px solid rgba(92,61,46,0.07)` }}>
              <div onClick={()=>onSelectP(p)} style={{ width:30, height:30, borderRadius:"50%", background:t?.bg||"#eee", display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, cursor:"pointer", flexShrink:0 }}>{t?.emoji}</div>
              <div style={{ flex:1, cursor:"pointer" }} onClick={()=>onSelectP(p)}>
                <div style={{ fontWeight:500, fontSize:12, color:C.navy, display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
                  {p.nick}
                  <span style={{ fontSize:10, color:C.walnut, fontWeight:400 }}>{p.email}</span>
                  {g&&<span style={{ fontSize:9, color:GENRE_COLORS[g], background:GENRE_COLORS[g]+"18", borderRadius:3, padding:"1px 5px", fontWeight:500 }}>{g}</span>}
                  {isNew&&<span style={{ fontSize:9, color:"white", background:"#dc2626", borderRadius:3, padding:"1px 5px", fontWeight:600 }}>최신</span>}
                  {teamOf&&<span style={{ fontSize:9, color:C.teal, background:"rgba(15,110,86,0.08)", borderRadius:3, padding:"1px 5px" }}>팀 {teamOf.id}</span>}
                </div>
                <div style={{ fontSize:11, color:C.walnut }}>{t?.name} · {p.vocalRange} · Pub: {p.pub==="yes"?p.pubName||"Yes":"No"}</div>
              </div>
              <button onClick={()=>onDeleteParticipant(p.email)} style={{ background:"none", border:`0.5px solid rgba(220,38,38,0.4)`, borderRadius:6, padding:"3px 8px", fontSize:11, color:"#dc2626", cursor:"pointer", flexShrink:0 }}>삭제</button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function App() {
  const [userRole, setUserRole] = useState(null);
  const [screen, setScreen] = useState("intro");
  const [curQ, setCurQ] = useState(0);
  const [answers, setAnswers] = useState(Array(QUESTIONS.length).fill(null));
  const [multiAnswers, setMultiAnswers] = useState(Array(QUESTIONS.length).fill(null).map(()=>[]));
  const [nick, setNick] = useState("");
  const [email, setEmail] = useState("");
  const [pub, setPub] = useState("none");
  const [pubName, setPubName] = useState("");
  const [result, setResult] = useState(null);
  const [suggestedTeam, setSuggestedTeam] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [lookup, setLookup] = useState("");
  const [lookupRes, setLookupRes] = useState([]);
  const [err, setErr] = useState("");
  const [adminPw, setAdminPw] = useState("");
  const [adminErr, setAdminErr] = useState("");
  const [adminAuthed, setAdminAuthed] = useState(false);
  const [brief, setBrief] = useState({ genre:"any", vocalRange:"any", language:"any", mood:"any", style:"any" });
  const [teams, setTeams] = useState(makeEmptyTeams);
  const [teamSize, setTeamSize] = useState(4);
  const [fbReady, setFbReady] = useState(false);
  const [prevResult, setPrevResult] = useState(null);
  const [checkingPrev, setCheckingPrev] = useState(true);
  const [selectedP, setSelectedP] = useState(null);
  const [adminTab, setAdminTab] = useState("teamboard");
  const [confirmReset, setConfirmReset] = useState(false);
  const [lastVisitTs, setLastVisitTs] = useState(null);

  useEffect(()=>{
  initFirebase().then(ok=>{
    setFbReady(ok);
    loadAll().then(setParticipants);
  });
  // 이전 결과 확인
  checkMyPreviousResult().then(prev => {
    setPrevResult(prev || null);
    setCheckingPrev(false);
  });
},[]);
  async function refreshParticipants() { loadAll().then(setParticipants); }

  const q = QUESTIONS[curQ];
  const isMulti = q.multi;
  const curMulti = multiAnswers[curQ]||[];
  const canNext = isMulti?curMulti.length>0:answers[curQ]!==null;
  const pct = Math.round((curQ/QUESTIONS.length)*100);

  function toggleMulti(i) {
    const prev=multiAnswers[curQ]||[], isNone=!q.options[i].scores||Object.keys(q.options[i].scores).length===0;
    let next;
    if(isNone){next=prev.includes(i)?[]:[i];}
    else{const f=prev.filter(x=>Object.keys(q.options[x].scores||{}).length>0);next=f.includes(i)?f.filter(x=>x!==i):[...f,i];}
    const u=[...multiAnswers];u[curQ]=next;setMultiAnswers(u);
  }
  function selectOpt(i) {
    const a=[...answers];a[curQ]=i;setAnswers(a);
    if(QUESTIONS[curQ].isRoleQuestion) setUserRole(QUESTIONS[curQ].options[i].role);
  }
  function nextQ(){if(!canNext)return;if(curQ<QUESTIONS.length-1)setCurQ(curQ+1);else setScreen("info");}
  function prevQ(){if(curQ>0)setCurQ(curQ-1);}

  async function submit() {
    if(!nick.trim()||!email.trim()){setErr("닉네임과 이메일을 입력해주세요");return;}
    setErr("");
    const scores=calcScores(answers,multiAnswers);
    const {type,roleMismatch,recommendedPosition}=assignType(scores,userRole);
    const p={ nick:nick.trim(),email:email.trim(),pub,pubName:pub==="yes"?pubName.trim():"",scores,typeId:type.id,userRole,roleMismatch,recommendedPosition,vocalRange:getVocalRange(answers),answers,multiAnswers:JSON.stringify(multiAnswers),ts:Date.now() };
    const list=await saveP(p);setParticipants(list||[]);
    const others=(list||[]).filter(x=>x.email!==p.email);
    const myType=TYPES.find(t=>t.id===type.id);
    let team=[];
    if(myType.role==="producer"||myType.role==="session"){
      const otherRole=myType.role==="producer"?"session":"producer";
      const preferred=myType.preferredTeam?.session||myType.preferredTeam?.producer||[];
      const rolePool=others.filter(o=>{const t=TYPES.find(x=>x.id===o.typeId);return t?.role===otherRole;});
      const sorted=rolePool.sort((a,b)=>(preferred.includes(b.typeId)?1:0)-(preferred.includes(a.typeId)?1:0));
      if(sorted[0])team.push(sorted[0]);
      const tlPool=others.filter(o=>{const t=TYPES.find(x=>x.id===o.typeId);return t?.role==="topliner"&&!team.find(m=>m.email===o.email);});
      const prefTl=myType.preferredTeam?.topliners||[];
      tlPool.sort((a,b)=>(prefTl.includes(b.typeId)?1:0)-(prefTl.includes(a.typeId)?1:0)).slice(0,2).forEach(m=>team.push(m));
    } else {
      const prefProd=myType.preferredTeam?.producer||[];
      const prodPool=others.filter(o=>{const t=TYPES.find(x=>x.id===o.typeId);return t?.role==="producer";});
      const sortedProd=prodPool.sort((a,b)=>(prefProd.includes(b.typeId)?1:0)-(prefProd.includes(a.typeId)?1:0));
      if(sortedProd[0])team.push(sortedProd[0]);
      const sessPool=others.filter(o=>{const t=TYPES.find(x=>x.id===o.typeId);return t?.role==="session"&&!team.find(m=>m.email===o.email);});
      if(sessPool[0])team.push(sessPool[0]);
      const prefCoTl=myType.preferredTeam?.coTopliner||[];
      const coTlPool=others.filter(o=>{const t=TYPES.find(x=>x.id===o.typeId);return t?.role==="topliner"&&!team.find(m=>m.email===o.email);});
      const sortedCoTl=coTlPool.sort((a,b)=>(prefCoTl.includes(b.typeId)?1:0)-(prefCoTl.includes(a.typeId)?1:0));
      if(sortedCoTl[0])team.push(sortedCoTl[0]);
    }
    setSuggestedTeam(team);setResult({scores,type,p,roleMismatch,recommendedPosition,userRole});setScreen("result");
  }

  function adminLogin() {
    if(adminPw===ADMIN_PASSWORD){
      // 마지막 방문 시간 읽기 → state 저장 → 현재 시간으로 업데이트
      const prev = parseInt(localStorage.getItem("sb_admin_last_visit")||"0", 10);
      setLastVisitTs(prev);
      localStorage.setItem("sb_admin_last_visit", String(Date.now()));
      setAdminAuthed(true);setAdminErr("");refreshParticipants();
    } else setAdminErr("비밀번호가 틀렸습니다");
  }

  async function deleteParticipant(email) {
    await fbDelete(email);
    loadAll().then(setParticipants);
  }

  async function resetAllParticipants() {
    for(const p of participants) await fbDelete(p.email);
    setParticipants([]);setConfirmReset(false);
  }

  function runAdminMatch(){
    const newTeams = findAllTeams(participants, brief, TEAM_COUNT, teamSize);
    setTeams(newTeams);
    setAdminTab("teamboard");
  }

  function doLookup(){
    const q2=lookup.trim().toLowerCase();if(!q2)return;
    setLookupRes(participants.filter(p=>p.nick.toLowerCase().includes(q2)||p.email.toLowerCase().includes(q2)));
  }

  function reset(){
    setUserRole(null);setScreen("intro");setCurQ(0);
    setAnswers(Array(QUESTIONS.length).fill(null));
    setMultiAnswers(Array(QUESTIONS.length).fill(null).map(()=>[]));
    setNick("");setEmail("");setPub("none");setPubName("");
    setResult(null);setSuggestedTeam(null);setLookup("");setLookupRes([]);setErr("");
  }

  const S={
    wrap:{ fontFamily:"var(--font-sans)", maxWidth:640, padding:"1.5rem 1rem", background:C.cream, minHeight:"100vh" },
    card:{ background:"white", border:`0.5px solid rgba(92,61,46,0.15)`, borderRadius:12, padding:"1.25rem", marginBottom:"1rem" },
    btn:{ background:"transparent", border:`0.5px solid rgba(12,35,64,0.25)`, borderRadius:8, padding:"9px 20px", fontSize:14, color:C.navy, cursor:"pointer" },
    btnP:{ background:C.orange, border:"none", borderRadius:8, padding:"9px 20px", fontSize:14, color:"white", cursor:"pointer" },
    btnTeal:{ background:C.teal, border:"none", borderRadius:8, padding:"9px 20px", fontSize:14, color:"white", cursor:"pointer" },
    opt:(sel)=>({ background:sel?"#FEF3ED":"rgba(92,61,46,0.04)", border:`0.5px solid ${sel?C.orange:"rgba(92,61,46,0.15)"}`, borderRadius:8, padding:"10px 14px", cursor:"pointer", fontSize:13, color:sel?C.orange:C.navy, textAlign:"left", width:"100%", marginBottom:8, lineHeight:1.5 }),
    input:{ width:"100%", padding:"8px 12px", fontSize:14, border:`0.5px solid rgba(12,35,64,0.2)`, borderRadius:8, background:"white", color:C.navy, marginTop:6 },
    hr:{ border:"none", borderTop:`0.5px solid rgba(92,61,46,0.15)`, margin:"1.5rem 0" },
    h1:{ fontSize:22, fontWeight:500, color:C.navy, marginBottom:4 },
    h2:{ fontSize:18, fontWeight:500, color:C.navy, marginBottom:"1rem" },
    sub:{ fontSize:13, color:C.walnut, lineHeight:1.7, marginBottom:"1.5rem" },
    label:{ fontSize:13, color:C.walnut },
  };

  const Header = () => (<div style={{ marginBottom:24 }}><div style={{ fontSize:11, color:C.walnut, letterSpacing:1 }}>STUDIO BORAN × THAIDAI</div></div>);

  if(screen==="admin") return (
    <div style={S.wrap}>
      <Header/>
      {selectedP&&<ParticipantModal participant={selectedP} onClose={()=>setSelectedP(null)}/>}
      {!adminAuthed ? (
        <div style={S.card}>
          <div style={S.h2}>관리자 로그인 / Admin Login</div>
          <input style={S.input} type="password" placeholder="Password" value={adminPw} onChange={e=>setAdminPw(e.target.value)} onKeyDown={e=>e.key==="Enter"&&adminLogin()}/>
          {adminErr&&<div style={{ fontSize:13, color:C.orange, marginTop:8 }}>{adminErr}</div>}
          <div style={{ display:"flex", gap:8, marginTop:12 }}>
            <button style={S.btn} onClick={()=>setScreen("intro")}>← 뒤로</button>
            <button style={S.btnP} onClick={adminLogin}>로그인</button>
          </div>
        </div>
      ) : (
        <div>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"1.5rem" }}>
            <div style={S.h2}>관리자 대시보드</div>
            <div style={{ display:"flex", gap:8 }}>
              <button style={S.btn} onClick={()=>setScreen("intro")}>← 뒤로</button>
              <button style={S.btn} onClick={()=>{setAdminAuthed(false);setScreen("intro");}}>로그아웃</button>
            </div>
          </div>
          {fbReady&&<div style={{ fontSize:11, color:C.teal, marginBottom:12 }}>✓ Firebase 연결됨</div>}

          <div style={{ display:"flex", gap:4, marginBottom:"1rem" }}>
            {[["teamboard","팀 편성표"],["match","팀 자동 추천"]].map(([key,label])=>(
              <button key={key} onClick={()=>setAdminTab(key)}
                style={{ padding:"7px 14px", fontSize:13, borderRadius:8, border:"none", cursor:"pointer", background:adminTab===key?C.navy:"rgba(12,35,64,0.06)", color:adminTab===key?"white":C.navy }}>
                {label}
              </button>
            ))}
          </div>

          {adminTab==="teamboard"&&(
            <div style={S.card}>
              <TeamBoard
                participants={participants} teams={teams} setTeams={setTeams} teamSize={teamSize}
                onDeleteParticipant={deleteParticipant} onSelectP={setSelectedP}
                confirmReset={confirmReset} setConfirmReset={setConfirmReset} resetAllParticipants={resetAllParticipants}
                lastVisitTs={lastVisitTs}
              />
            </div>
          )}

          {adminTab==="match"&&(
            <div style={S.card}>
              <div style={{ fontWeight:500, fontSize:15, color:C.navy, marginBottom:4 }}>캠프 브리프 / Camp Brief</div>
              <div style={{ fontSize:12, color:C.walnut, marginBottom:14, lineHeight:1.6 }}>
                브리프를 설정하고 자동 배치하면 팀 편성표에 최적 조합이 채워집니다.<br/>
                장르 우선순위 → 롤별 능력치 순 → 스네이크 드래프트로 고른 분배
              </div>
              <div style={{ marginBottom:14 }}>
                <div style={{ fontSize:13, color:C.walnut, marginBottom:6 }}>팀 인원수</div>
                <div style={{ display:"flex", gap:8 }}>
                  {[3,4].map(n=>(
                    <button key={n} onClick={()=>setTeamSize(n)}
                      style={{ padding:"6px 20px", borderRadius:8, border:`0.5px solid ${teamSize===n?C.teal:"rgba(12,35,64,0.2)"}`, background:teamSize===n?"rgba(15,110,86,0.08)":"white", color:teamSize===n?C.teal:C.navy, cursor:"pointer", fontSize:13, fontWeight:teamSize===n?600:400 }}>
                      {n}명 {n===3?"(프로듀서+탑라이너×2)":"(프로듀서+세션+탑라이너×2)"}
                    </button>
                  ))}
                </div>
              </div>
              {[
                { label:"장르 우선", key:"genre", opts:[{v:"any",l:"전체 (장르 무관)"},{v:"dance",l:"댄스/팝"},{v:"ballad",l:"발라드/R&B"},{v:"hiphop",l:"힙합/어반"},{v:"indie",l:"인디/실험"}] },
                { label:"아티스트 무드", key:"mood", opts:[{v:"any",l:"전체"},{v:"bright",l:"☀️ 청량한 신인 바이브 우선"},{v:"dark",l:"🌙 다크/몽환 섹시 우선"}] },
                { label:"보컬/랩 비중", key:"style", opts:[{v:"any",l:"전체"},{v:"balanced",l:"균형 (보컬·랩 교대 배치)"},{v:"vocalist",l:"보컬 우선"},{v:"rapper",l:"랩/멜로디 랩 우선"}] },
                { label:"보컬 레인지 (탑라이너 우선)", key:"vocalRange", opts:[{v:"any",l:"전체"},{v:"soprano",l:"소프라노/메조"},{v:"alto",l:"알토"},{v:"tenor",l:"테너"},{v:"baritone",l:"바리톤/베이스"}] },
                { label:"언어 필터", key:"language", opts:[{v:"any",l:"전체"},{v:"bilingual",l:"이중언어 가능자 우선"}] },
              ].map(row=>(
                <div key={row.key} style={{ marginBottom:12 }}>
                  <div style={S.label}>{row.label}</div>
                  <select style={S.input} value={brief[row.key]} onChange={e=>setBrief({...brief,[row.key]:e.target.value})}>
                    {row.opts.map(o=><option key={o.v} value={o.v}>{o.l}</option>)}
                  </select>
                </div>
              ))}
              <div style={{ display:"flex", gap:8, marginTop:4 }}>
                <button style={S.btnTeal} onClick={runAdminMatch}>자동 배치 실행 / Auto-Place Teams</button>
                <button style={S.btn} onClick={()=>{ setTeams(makeEmptyTeams()); setAdminTab("teamboard"); }}>편성표 초기화</button>
              </div>
              <div style={{ fontSize:11, color:C.walnut, marginTop:10, opacity:0.65 }}>실행 후 팀 편성표 탭으로 자동 이동합니다</div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  if(screen==="intro") return (
    <div style={S.wrap}>
      <Header/>
      <div style={S.h1}>Song Camp Role Finder</div>
      <div style={{ fontSize:12, color:C.walnut, marginBottom:16 }}>내 유형을 찾고, 팀을 완성하세요 / Find your type, complete your team</div>
      <div style={S.sub}>
        당신은 어떤 뮤지션인가요?<br/>
        {TOTAL_QUESTIONS}개 질문으로 역할을 진단하고 최적의 팀원을 찾아드려요.<br/><br/>
       {TOTAL_QUESTIONS} questions · 10 types · Find your perfect song camp team.
      </div>
      {!checkingPrev && prevResult && (
        <div style={{ background:"#FFF8E1", border:"0.5px solid #F59E0B", borderRadius:10, padding:"12px 16px", marginBottom:"1rem", fontSize:13, color:"#92400E", lineHeight:1.8 }}>
          <div style={{ fontWeight:500, marginBottom:6 }}>앗, 기존의 테스트 결과가 있네요. 다시 한번 정밀하게 알아볼까요?</div>
          <div style={{ fontSize:12, opacity:0.75, marginBottom:10 }}>이전 닉네임: <strong>{prevResult.nick}</strong></div>
          <button onClick={async () => { await clearMyResult(prevResult.email); setPrevResult(null); loadAll().then(setParticipants); }}
            style={{ background:"#C4510A", border:"none", borderRadius:7, padding:"6px 14px", fontSize:12, color:"white", cursor:"pointer" }}>
            이전 결과 초기화하고 다시 시작
          </button>
        </div>
      )}
      <div style={{ display:"flex", gap:12, alignItems:"center", marginBottom:"2rem" }}>
        <button style={S.btnP} onClick={()=>setScreen("quiz")}>시작하기 / Start</button>
        <span style={{ fontSize:12, color:C.walnut }}>약 5분 / ~5 min</span>
      </div>
      <hr style={S.hr}/>
      <div style={{ fontSize:13, fontWeight:500, color:C.walnut, marginBottom:8 }}>팀원 조회 / Look up participants</div>
      <div style={{ display:"flex", gap:8, marginBottom:8 }}>
        <input style={{ ...S.input, marginTop:0, flex:1 }} placeholder="닉네임 또는 이메일" value={lookup} onChange={e=>setLookup(e.target.value)} onKeyDown={e=>e.key==="Enter"&&doLookup()}/>
        <button style={S.btn} onClick={doLookup}>조회</button>
      </div>
      {lookup&&lookupRes.length===0&&<div style={{ fontSize:13, color:C.walnut }}>결과 없음</div>}
      {lookupRes.map((p,i)=>{ const t=TYPES.find(x=>x.id===p.typeId); return(
        <div key={i} style={{ ...S.card, marginBottom:8 }}>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <div style={{ width:36, height:36, borderRadius:"50%", background:t?.bg||"#eee", display:"flex", alignItems:"center", justifyContent:"center", fontSize:18 }}>{t?.emoji}</div>
            <div><div style={{ fontWeight:500, fontSize:14, color:C.navy }}>{p.nick}</div><div style={{ fontSize:12, color:C.walnut }}>{t?`${t.name} / ${t.en}`:p.typeId}</div></div>
          </div>
        </div>
      );})}
      <CreditFooter/>
      <div style={{ textAlign:"center", marginTop:"1rem" }}>
        <button style={{ ...S.btn, fontSize:11, opacity:0.4 }} onClick={()=>setScreen("admin")}>관리자 / Admin</button>
      </div>
    </div>
  );

  if(screen==="quiz") return (
    <div style={S.wrap}>
      <Header/>
      <div style={{ height:3, background:"rgba(92,61,46,0.12)", borderRadius:2, marginBottom:"1.5rem" }}>
        <div style={{ height:"100%", width:pct+"%", background:C.orange, borderRadius:2, transition:"width 0.3s" }}/>
      </div>
      <div style={{ fontSize:11, color:C.walnut, marginBottom:10 }}>Q{curQ+1} / {QUESTIONS.length}</div>
      <div style={S.card}>
        <div style={{ fontSize:15, fontWeight:500, color:C.navy, marginBottom:q.sub?4:14, lineHeight:1.6 }}>
          {q.text.split("\n").map((l,i)=><div key={i}>{l}</div>)}
        </div>
        {q.sub&&<div style={{ fontSize:11, color:C.walnut, marginBottom:14 }}>{q.sub}</div>}
        {isMulti&&<div style={{ fontSize:11, color:C.teal, marginBottom:10 }}>복수 선택 가능 / Multiple selection allowed</div>}
        {q.options.map((o,i)=>{ const sel=isMulti?curMulti.includes(i):answers[curQ]===i; return (
          <button key={i} style={S.opt(sel)} onClick={()=>isMulti?toggleMulti(i):selectOpt(i)}>
            <div style={{ display:"flex", alignItems:"flex-start", gap:8 }}>
              {isMulti&&<div style={{ width:14, height:14, borderRadius:3, border:`1.5px solid ${sel?C.orange:"rgba(12,35,64,0.2)"}`, background:sel?C.orange:"transparent", flexShrink:0, marginTop:2 }}/>}
              <div><div>{o.ko}</div><div style={{ fontSize:11, opacity:0.65, marginTop:2 }}>{o.en}</div></div>
            </div>
          </button>
        );})}
      </div>
      <div style={{ display:"flex", justifyContent:"space-between" }}>
        <button style={{ ...S.btn, opacity:curQ===0?0.3:1 }} onClick={prevQ} disabled={curQ===0}>이전 / Prev</button>
        <button style={{ ...S.btnP, opacity:canNext?1:0.35 }} onClick={nextQ} disabled={!canNext}>{curQ===QUESTIONS.length-1?"완료 / Done":"다음 / Next"}</button>
      </div>
    </div>
  );

  if(screen==="info") return (
    <div style={S.wrap}>
      <Header/>
      <div style={S.h2}>거의 다 됐어요! / Almost there!</div>
      <div style={S.sub}>기본 정보를 입력하면 유형과 팀 미리보기를 볼 수 있어요.</div>
      <div style={S.card}>
        <div style={{ marginBottom:"1rem" }}>
          <div style={S.label}>닉네임 / Nickname *</div>
          <input style={S.input} placeholder="ex. BeatMaker_J" value={nick} onChange={e=>setNick(e.target.value)}/>
        </div>
        <div style={{ marginBottom:"1rem" }}>
          <div style={S.label}>이메일 / Email *</div>
          <input style={S.input} type="email" placeholder="ex. you@email.com" value={email} onChange={e=>setEmail(e.target.value)}/>
        </div>
        <div style={{ marginBottom:pub==="yes"?"0.5rem":"1rem" }}>
          <div style={S.label}>퍼블리셔 있음? / Publisher?</div>
          <select style={S.input} value={pub} onChange={e=>setPub(e.target.value)}>
            <option value="none">없음 / No</option>
            <option value="yes">있음 / Yes</option>
            <option value="unsure">잘 모름 / Not sure</option>
          </select>
        </div>
        {pub==="yes"&&(
          <div style={{ marginBottom:"1rem", paddingLeft:12, borderLeft:`2px solid ${C.tealMid}` }}>
            <div style={S.label}>퍼블리셔 이름 / Publisher name</div>
            <input style={S.input} placeholder="ex. KOMCA, Sony Music Publishing..." value={pubName} onChange={e=>setPubName(e.target.value)}/>
          </div>
        )}
        {err&&<div style={{ fontSize:13, color:C.orange }}>{err}</div>}
      </div>
      <div style={{ display:"flex", gap:8 }}>
        <button style={S.btn} onClick={()=>setScreen("quiz")}>이전 / Back</button>
        <button style={S.btnP} onClick={submit}>결과 보기 / See my type</button>
      </div>
    </div>
  );

  if(screen==="result"&&result) {
    const {scores,type,roleMismatch,recommendedPosition,userRole:savedRole}=result;
    return (
      <div style={S.wrap}>
        <Header/>
        <div style={{ fontSize:11, color:C.walnut, marginBottom:8, letterSpacing:1 }}>당신의 유형 / YOUR TYPE</div>
        <div style={{ display:"inline-flex", alignItems:"center", gap:8, padding:"6px 14px", borderRadius:20, background:type.bg, marginBottom:14 }}>
          <span style={{ fontSize:18 }}>{type.emoji}</span>
          <span style={{ fontSize:14, fontWeight:500, color:type.color }}>{type.name} / {type.en}</span>
        </div>
        <div style={{ fontSize:24, fontWeight:500, color:C.navy, marginBottom:12 }}>{type.emoji} {type.name}</div>
        <div style={{ fontSize:13, color:C.walnut, lineHeight:1.8, marginBottom:6 }}>{type.desc.split("\n").map((l,i)=><div key={i}>{l}</div>)}</div>
        <div style={{ fontSize:12, color:C.walnut, lineHeight:1.8, marginBottom:"1.5rem", opacity:0.75 }}>{type.en_desc.split("\n").map((l,i)=><div key={i}>{l}</div>)}</div>
        {roleMismatch&&(
          <div style={{ background:"#FFF8E1", border:"0.5px solid #F59E0B", borderRadius:10, padding:"12px 16px", marginBottom:"1rem", fontSize:13, color:"#92400E", lineHeight:1.7 }}>
            <strong>💡 포지션 제안</strong><br/>
            본인은 <strong>{savedRole==="producer"?"프로듀서":savedRole==="topliner"?"탑라이너":"세션"}</strong>로 선택하셨지만,
            테스트 결과 <strong>{recommendedPosition==="producer"?"프로듀서":recommendedPosition==="topliner"?"탑라이너":"세션"}</strong> 포지션이 더 잘 맞을 수 있어요.
          </div>
        )}
        <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:"1rem" }}>
          {AXES_KEYS.map((k,i)=>(
            <div key={k} style={{ background:"white", border:`0.5px solid rgba(92,61,46,0.15)`, borderRadius:8, padding:"6px 12px", fontSize:12 }}>
              <strong style={{ color:C.navy }}>{Math.min(8,scores[k]||0)}</strong>
              <span style={{ color:C.walnut, marginLeft:4 }}>{AXES_LABELS[i]}</span>
            </div>
          ))}
        </div>
        <div style={{ display:"flex", justifyContent:"center", margin:"1rem 0 1.5rem" }}><RadarChart scores={scores}/></div>
        <hr style={S.hr}/>
        <div style={S.h2}>팀 미리보기 / Team Preview</div>
        <div style={{ ...S.sub, marginBottom:"0.5rem" }}>함께하면 시너지가 높을 것 같은 유형이에요.<br/>Based on your type, here are your ideal teammates.</div>
        {suggestedTeam&&suggestedTeam.length>0?(
          suggestedTeam.map((m,i)=>{ const t=TYPES.find(x=>x.id===m.typeId); return(
            <div key={i} style={{ ...S.card, display:"flex", alignItems:"center", gap:12, marginBottom:8 }}>
              <div style={{ width:40, height:40, borderRadius:"50%", background:t?.bg||"#eee", display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, flexShrink:0 }}>{t?.emoji}</div>
              <div><div style={{ fontWeight:500, fontSize:14, color:C.navy }}>{maskName(m.nick)}</div><div style={{ fontSize:12, color:C.walnut }}>{t?`${t.name} / ${t.en}`:m.typeId}</div></div>
            </div>
          );})
        ):(
          <div style={{ textAlign:"center", padding:"2rem", color:C.walnut, fontSize:13, background:"white", borderRadius:12, marginBottom:"1rem" }}>아직 다른 참가자가 없어요.<br/>No other participants yet — share this quiz!</div>
        )}
        <div style={{ fontSize:11, color:C.walnut, opacity:0.55, textAlign:"center", marginBottom:"1.5rem", lineHeight:1.7 }}>
          * 팀원 닉네임은 개인정보 보호를 위해 첫 글자만 표시됩니다.<br/>
          * 실제 팀 구성은 기획사 브리프에 따라 달라질 수 있습니다.
        </div>
        <CreditFooter/>
        <div style={{ textAlign:"center", marginTop:"1rem" }}>
          <button style={S.btn} onClick={reset}>처음으로 / Start over</button>
        </div>
      </div>
    );
  }

  return null;
}